import { defineStore } from 'pinia';
import type { Breadcrumbs } from 'src/types/Braedcrumbs';
import type {
  EeventActions,
  HeaderActions,
  HeaderActionItems,
} from 'src/types/HeaderActions';
import {
  mdiHandBackRightOutline,
  mdiInformationVariantCircleOutline,
  mdiListStatus,
  mdiMenu,
  mdiRestart,
  mdiStar,
  mdiStarOutline,
} from '@quasar/extras/mdi-v7';

export interface State {
  total?: number;
  title: string;
  subtitle?: string;
  alignTextCenter?: boolean;
  breadcrumbs?: Breadcrumbs;
  showBreadcrumbs?: boolean;
  headerActions?: HeaderActions;
}

const headerActionItems: HeaderActionItems = {
  'action:instruction': {
    icon: mdiInformationVariantCircleOutline,
    label: 'INSTRUCTIONS',
    action: 'action:instruction',
    order: 10,
  },
  'action:help': {
    icon: mdiHandBackRightOutline,
    label: 'HELP',
    action: 'action:help',
    order: 20,
  },
  'action:interim': {
    icon: mdiListStatus,
    label: 'INTERIM_RESULTS',
    action: 'action:interim',
    order: 30,
  },
  'action:reset': {
    icon: mdiRestart,
    label: 'RESET',
    action: 'action:reset',
    order: 40,
  },
  'action:favorite': {
    icon: mdiStarOutline,
    label: 'FAVORITE',
    action: 'action:favorite',
    order: 50,
  },
  'action:unfavorite': {
    icon: mdiStar,
    label: 'UNFAVORITE',
    action: 'action:unfavorite',
    order: 60,
  },
  'action:toggle-drawer': {
    icon: mdiMenu,
    label: 'TOGGLE_DRAWER',
    action: 'action:toggle-drawer',
    order: 70,
  },
};

export const useGlobalStore = defineStore('global', {
  state: (): State => ({
    total: undefined,
    title: '',
    subtitle: undefined,
    breadcrumbs: [],
    alignTextCenter: true,
    showBreadcrumbs: true,
    headerActions: [],
  }),
  actions: {
    setTotal(total: number | undefined) {
      this.total = total;
    },
    setTitle(title: string) {
      this.title = title;
    },
    setBreadcrumbs(breadcrumbs: Array<{ label: string; link?: string }>) {
      this.breadcrumbs = breadcrumbs;
    },
    setHeaderActions(headerActions: EeventActions[]) {
      this.headerActions = headerActions.map(
        (action) => headerActionItems[action],
      );
    },
    addHeaderAction(headerAction: EeventActions) {
      if (!headerAction || !headerActionItems?.[headerAction]) return;
      if (this.headerActions?.some((ha) => ha.action === headerAction)) return;
      if (!this.headerActions) this.headerActions = [];
      this.headerActions.push(headerActionItems[headerAction]);
    },
    clearHeaderActions() {
      this.headerActions = [];
    },
    removeHeaderAction(action: EeventActions) {
      if (!this.headerActions || !action) {
        return;
      }
      this.headerActions = this.headerActions.filter(
        (ha) => ha.action !== action,
      );
    },
  },
  getters: {
    orderedHeaderActions: (state) => {
      return (
        state?.headerActions?.sort((a, b) => (a.order || 0) - (b.order || 0)) ||
        []
      );
    },
  },
});
